/* eslint max-lines: off */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useAsync } from 'react-use';
import { useHistory } from 'react-router';

import { OpenConvalidaInfoResponse, Group, HistoryRecord } from 'models/response';

import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';
import actions from 'state/actions';

import TextArea from 'components/Input/Textarea';
import Table from 'components/Table';
import Modal from 'components/Modal';
import styled from 'styled-components/macro';

import Groups from './groups';
import Close from './close';
import { CloseConvalidateInfoDocumentBody } from 'models/request';

const Section = styled.div`
  margin: 30px 0;
`;

export const Title = styled.h4`
  margin-bottom: 10px;
`;

type Mode = 'groups' | 'close';

const ConvalidateInfo = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const [openConvalidateInfoResponse, setOpenConvalidateInfoResponse] = useState<OpenConvalidaInfoResponse | null>(
    null,
  );
  const [rowsHistory, setRowsHistory] = useState<HistoryRecord[]>([]);
  const [note, setNote] = useState<string>('');
  const history = useHistory();

  const [mode, setMode] = useState<Mode | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const userPreference = useSelector(selectors.user.getUserPreference);

  useAsync(async () => {
    try {
      if (protocol) {
        const { data } = await service.openConvalidateInfoDocument(protocol);
        setRowsHistory(data.wfMovements);
        setOpenConvalidateInfoResponse(data);
        const getMode = () => {
          if (data.groups && data.groups.length) {
            setSelectedGroups([data.groups[0]]);
            return 'groups';
          } else if (data.finalDestinationUser) {
            return 'close';
          } else {
            return null;
          }
        };
        setMode(getMode());
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const storicoColumns = [
    {
      accessor: 'causalCode',
      Header: t('causalCode'),
    },
    { accessor: 'note', Header: t('note') },
    { accessor: 'protocol', Header: t('protocol') },
    {
      accessor: 'rejectionReason',
      Header: t('rejectionReason'),
      Cell: ({ value }: { value: null | string }) => value ?? '',
    },
    {
      accessor: 'sendDate',
      Header: t('sendDate'),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
    },
    {
      accessor: 'type',
      Header: t('type'),
    },
    {
      accessor: 'typeDescription',
      Header: t('typeDescription'),
    },
  ];

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const handleConfirm = async () => {
    try {
      if (protocol && openConvalidateInfoResponse) {
        const body = ((): CloseConvalidateInfoDocumentBody | null => {
          switch (mode) {
            case 'groups':
              return {
                protocol,
                note,
                finalDestinationUser: null,
              };
            case 'close':
              return {
                protocol,
                note,
                finalDestinationUser:
                  openConvalidateInfoResponse.finalDestinationUser?.idUser ||
                  openConvalidateInfoResponse.finalDestinationUser?.idUser === 0
                    ? 0
                    : null,
              };
            default:
              return null;
          }
        })();
        await service.closeConvalidateInfoDocument(body);
        dispatch(actions.modal.closeModal());
        dispatch(actions.documents.removeRows([protocol]));
        if (utils.documents.isOpenDocumentView()) dispatch(actions.documents.openNextDocument(history));
        utils.app.notify('success', t('success_convalidate_info'));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const renderContent = (mod?: 'groups' | 'close') => {
    switch (mod) {
      case 'groups':
        return openConvalidateInfoResponse ? (
          <Groups response={openConvalidateInfoResponse} selectedGroups={selectedGroups} />
        ) : null;
      case 'close':
        return openConvalidateInfoResponse ? <Close response={openConvalidateInfoResponse} /> : null;
      default:
        return null;
    }
  };

  return (
    <>
      <Modal.Header title={t('confirm-title')} subtitle={t('confirm-desc')} />
      <Modal.Content>
        {rowsHistory.length ? (
          <>
            <Title>{t('storico')}</Title>
            <Table id="convalidate-history-table" hasPagination hasSort columns={storicoColumns} rows={rowsHistory} />
          </>
        ) : null}
        {mode ? renderContent(mode) : null}
        <Section>
          <Title>{t('note')}</Title>
          <TextArea value={note} name="Nota" placeholder={t('note here')} onChange={onNoteChange} />
        </Section>
      </Modal.Content>
      <Modal.Footer
        confirmText={t('continue')}
        confirmAction={handleConfirm}
        confirmDisabled={mode === 'groups' ? !selectedGroups.length : false}
      />
    </>
  );
};

export default ConvalidateInfo;
